import React, { useEffect } from "react";
import { Button, Fieldset, Group, Text } from "@mantine/core";
import { useFieldArray, useFormState, useWatch } from "react-hook-form";
import { TextInput, Select } from "react-hook-form-mantine";
import {
  IconPlus,
  IconX,
  IconBrandLinkedin,
  IconBrandTwitter,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandYoutube,
  IconBrandGithub,
  IconWorld,
  IconCheck,
  IconMail,
} from "@tabler/icons-react";
import { getUrlPlaceholder } from "../utils/urlValidation";

// Social platform definitions with their display names and icons
const SOCIAL_PLATFORMS = [
  {
    value: "linkedin",
    label: "LinkedIn",
    leftSection: <IconBrandLinkedin size={16} />,
  },
  {
    value: "twitter",
    label: "Twitter",
    leftSection: <IconBrandTwitter size={16} />,
  },
  {
    value: "facebook",
    label: "Facebook",
    leftSection: <IconBrandFacebook size={16} />,
  },
  {
    value: "instagram",
    label: "Instagram",
    leftSection: <IconBrandInstagram size={16} />,
  },
  {
    value: "youtube",
    label: "YouTube",
    leftSection: <IconBrandYoutube size={16} />,
  },
  {
    value: "github",
    label: "GitHub",
    leftSection: <IconBrandGithub size={16} />,
  },
  {
    value: "email",
    label: "Email",
    leftSection: <IconMail size={16} />,
  },
  {
    value: "website",
    label: "Website",
    leftSection: <IconWorld size={16} />,
  },
];

const LINK_TYPE_OPTIONS = [
  { value: "external", label: "External" },
  { value: "social", label: "Social" },
];

const renderSocialPlatformOption = ({ option, checked }) => {
  return (
    <Group flex="1" gap="xs">
      {option.leftSection}
      <Text size="sm">{option.label}</Text>
      {checked && <IconCheck style={{ marginInlineStart: "auto" }} />}
    </Group>
  );
};

const ExternalLinksInput = ({ control, setValue, name = "links" }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  // Get errors from control
  const { errors } = useFormState({ control });

  // Watch all field values to handle conditional logic
  const watchedFields = useWatch({
    control,
    name,
  });

  // Handle type changes with useEffect
  useEffect(() => {
    if (watchedFields && Array.isArray(watchedFields)) {
      watchedFields.forEach((field, index) => {
        if (field?.type === "social" && field?.platform) {
          const selectedPlatform = SOCIAL_PLATFORMS.find(
            (p) => p.value === field.platform
          );
          if (selectedPlatform && field.title !== selectedPlatform.label) {
            setValue(`${name}.${index}.title`, selectedPlatform.label);
          }
        }
      });
    }
  }, [watchedFields, setValue, name]);

  // Function to add a new link
  const addLink = () => {
    append({
      type: "external",
      title: "",
      platform: "",
      url: "",
    });
  };

  return (
    <Fieldset
      legend="External Links"
      mt="md"
      styles={{
        root: {
          // Color the border red if there are errors
          borderColor: errors?.links?.root?.message
            ? "var(--mantine-color-error)"
            : undefined,
        },
      }}
    >
      {errors?.links?.root?.message && (
        <Text c="red" size="sm">
          {errors?.links?.root?.message}
        </Text>
      )}
      {fields.map((field, index) => {
        const currentType = watchedFields?.[index]?.type || "external";
        const currentPlatform = watchedFields?.[index]?.platform || "";

        return (
          <Fieldset mt="md" key={field.id}>
            <Select
              name={`${name}.${index}.type`}
              control={control}
              label="Link Type"
              placeholder="Select link type"
              data={LINK_TYPE_OPTIONS}
              allowDeselect={false}
            />

            {currentType === "social" && (
              <Select
                name={`${name}.${index}.platform`}
                control={control}
                label="Social Platform"
                placeholder="Select platform"
                data={SOCIAL_PLATFORMS}
                renderOption={renderSocialPlatformOption}
                allowDeselect={false}
              />
            )}

            {currentType === "external" && (
              <TextInput
                name={`${name}.${index}.title`}
                control={control}
                label="Link Title"
                placeholder="Website, Documentation, etc."
              />
            )}

            <TextInput
              name={`${name}.${index}.url`}
              control={control}
              label="URL"
              placeholder={
                currentType === "social" && currentPlatform
                  ? getUrlPlaceholder(currentPlatform)
                  : "https://example.com"
              }
            />

            <Button
              mt={5}
              size="xs"
              color="red"
              onClick={() => remove(index)}
              leftSection={<IconX size={20} />}
            >
              Remove link
            </Button>
          </Fieldset>
        );
      })}

      <Button
        mt={5}
        size="xs"
        onClick={addLink}
        leftSection={<IconPlus size={20} />}
      >
        Add link
      </Button>
    </Fieldset>
  );
};

export default ExternalLinksInput;
